import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, Loader2 } from 'lucide-react';
import { authService, getPostAuthRoute, setPendingVerificationContext, setTokens, setUserProfile } from '../services/auth.service';
import GoogleAuthButton from '../components/auth/GoogleAuthButton';
import AuthLayout from '../components/auth/AuthLayout';

const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(''); // Clear error on change
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Basic validation
    if (!formData.email || !formData.password) {
      setError('Please fill in all fields.');
      return;
    }

    setLoading(true);

    try {
      const response = await authService.login(formData.email, formData.password);

      if (response.action === 1) {
        setTokens(response.token, response.refreshToken);
        setUserProfile(response);
        navigate(getPostAuthRoute(response), { replace: true });
      } else if (response.action === 0 && response.otpVerified === false && response.customerId) {
        setPendingVerificationContext({
          customerId: response.customerId,
          role: response.customerType || 'RENTER',
          purpose: 'signup'
        });
        navigate('/verify', {
          state: {
            role: response.customerType || 'RENTER',
            customerId: response.customerId,
            purpose: 'signup'
          }
        });
      } else if (response.action === 2 && response.requiresTwoFactor && response.customerId) {
        setPendingVerificationContext({
          customerId: response.customerId,
          role: response.customerType || 'RENTER',
          purpose: 'login_2fa'
        });
        navigate('/verify', {
          state: {
            role: response.customerType || 'RENTER',
            customerId: response.customerId,
            purpose: 'login_2fa'
          }
        });
      } else {
        setError(response.message || 'Login failed.');
      }
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>

          <div className="mb-6 text-center lg:text-left">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-brand-black mb-3">Welcome Back</h2>
            <p className="text-gray-500 text-sm">Log in to manage your bookings and plan your next journey.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm font-medium">
                {error}
              </div>
            )}
            <div>
              <label className="block text-xs font-bold text-brand-black uppercase tracking-wide mb-2">Email Address</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="name@company.com"
                className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-brand-orange-500 transition-all placeholder:text-gray-400 text-sm bg-white"
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-brand-black uppercase tracking-wide mb-2">Password</label>
              <div className="relative">
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="••••••••"
                  className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-brand-orange-500 transition-all placeholder:text-gray-400 text-sm bg-white"
                  disabled={loading}
                />
                <button type="button" className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <Eye className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between pt-2 mb-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="w-4 h-4 rounded border-gray-300 text-brand-black focus:ring-orange-200" />
                <span className="text-sm text-gray-600 font-medium">Remember me</span>
              </label>
              <Link to="/forgot-password" name="forgot-password-link" className="text-sm font-bold text-brand-black hover:underline">Forgot Password?</Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-brand-orange-500 text-white py-3 rounded-xl font-bold hover:bg-brand-orange-600 transition-colors mb-6 shadow-sm flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading && <Loader2 className="w-5 h-5 animate-spin" />}
              {loading ? 'Signing in...' : 'Sign In'}
            </button>

            <div className="relative flex items-center justify-center my-5">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <span className="relative bg-[#faf8f5] px-4 text-xs font-medium text-gray-400 uppercase tracking-wider">OR</span>
            </div>

            <GoogleAuthButton
              navigate={navigate}
              onError={setError}
              className="w-full overflow-hidden rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-sm"
            />
          </form>

          <p className="mt-8 text-center text-sm text-gray-500">
            Don't have an account? <Link to="/register" className="font-bold text-brand-black hover:underline">Join Go Rides</Link>
          </p>
    </AuthLayout>
  );
};

export default Login;
